import { ArrowLeft } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import homeService, { HomePageData } from "../../services/HomeService";
import { useQuery } from "@tanstack/react-query";
import { useHomeDataForDate } from "../../hooks/useHomeDataForDate";

const FoodScoreDetail = () => {
  const navigate = useNavigate();

  // Fetch home page data respecting the selected date
  const {
    data: homeData,
    isLoading: loading,
    selectedDate,
    formattedSelectedDate,
  } = useHomeDataForDate();

  // Get food score from API
  const foodScore = homeData?.["Food Score"] || 0;
  const maxScore = 60;
  const scorePercentage = (foodScore / maxScore) * 100;

  // Get status based on score
  const getScoreStatus = (score: number) => {
    if (score >= 50) {
      return { label: "Excellent", color: "text-green-600" };
    }
    if (score >= 40) {
      return { label: "Good", color: "text-blue-600" };
    }
    if (score >= 30) {
      return { label: "Fair", color: "text-yellow-600" };
    }
    return { label: "Needs Improvement", color: "text-red-600" };
  };

  const status = getScoreStatus(foodScore);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-subhead text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-bold text-foreground">
            {formattedSelectedDate} Food Score
          </h1>
          <div className="h-10 w-10" />
        </div>

        {/* Today's Stats */}
        <Card className="rounded-2xl border-0 bg-gradient-to-br from-chart-1/10 to-chart-1/5 p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-subhead text-muted-foreground">
              Today's Food Score
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">
              {foodScore}
            </div>
            <p className="text-subhead text-muted-foreground">
              of {maxScore} maximum
            </p>
            <p className={`mt-2 text-subhead font-semibold ${status.color}`}>
              {status.label}
            </p>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-chart-1 transition-all"
                style={{ width: `${scorePercentage}%` }}
              />
            </div>
          </div>
        </Card>

        {/* What is Food Score */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Food Score?
          </h3>
          <p className="text-subhead leading-relaxed text-muted-foreground">
            Food Score measures the quality of your dietary intake on a scale of
            0-60. It evaluates nutrient density, whole food consumption, and the
            balance of macronutrients in your meals. A higher score indicates
            better nutritional choices that support gut health and overall
            wellbeing.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Excellent</span>
              <span className="font-semibold">50-60</span>
            </div>
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Good</span>
              <span className="font-semibold">40-49</span>
            </div>
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Fair</span>
              <span className="font-semibold">30-39</span>
            </div>
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Needs Improvement</span>
              <span className="font-semibold">0-29</span>
            </div>
          </div>
        </Card>

        {/* Improvement Tips */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">Ways to Improve</h3>
          <ul className="space-y-2 text-subhead text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-1">•</span>
              <span>Increase variety of colorful vegetables in your meals</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-1">•</span>
              <span>Choose whole grains over refined carbohydrates</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-1">•</span>
              <span>Include more fermented foods for gut health</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-chart-1">•</span>
              <span>Prioritize omega-3 rich foods like fish and nuts</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default FoodScoreDetail;
